import { useEffect, useRef, RefObject } from 'react';

/**
 * A shared "should this canvas be drawing right now?" signal.
 *
 * Returns a ref whose `.current` is false when the tab is hidden or the element
 * has scrolled out of view. A render loop reads it once per frame and bails
 * early; it stays a ref rather than state so checking it never re-renders.
 *
 * This exists because an animation loop with no gate is a real defect, not a
 * nitpick: a portfolio page that keeps a CPU core busy in a background tab
 * drains a laptop and spins a fan on someone who has already moved on.
 *
 * Usage:
 *   const visible = useRenderGate(hostRef);
 *   const frame = () => { if (visible.current) draw(); raf = rAF(frame); };
 */
export function useRenderGate(
    ref: RefObject<Element | null>,
    { rootMargin = '120px' }: { rootMargin?: string } = {},
): RefObject<boolean> {
    const visible = useRef(true);

    useEffect(() => {
        const el = ref.current;
        let inView = true;
        let onScreen = !document.hidden;

        const sync = () => { visible.current = inView && onScreen; };

        const onVis = () => { onScreen = !document.hidden; sync(); };
        document.addEventListener('visibilitychange', onVis);

        let io: IntersectionObserver | undefined;
        if (el && typeof IntersectionObserver !== 'undefined') {
            io = new IntersectionObserver(
                entries => {
                    inView = entries.some(e => e.isIntersecting);
                    sync();
                },
                { rootMargin },
            );
            io.observe(el);
        }

        sync();
        return () => {
            document.removeEventListener('visibilitychange', onVis);
            io?.disconnect();
        };
    }, [ref, rootMargin]);

    return visible;
}
